/**!*******************************************************************
 *
 * Copyright (c) Epic AI GmbH. All rights reserved.
 * Author:  Alexander Claas
 * 
 ********************************************************************!*/

import { fetchCloudRun } from "../Helper";
import { IEstimatedInvoiceResult, IInvoiceResult, IPaymentMethodResult, IPromotionResult, ISubscriptionResult, IUpcomingInvoiceResult } from "@chatcaptain/types/billing";
import { useStore } from "@/store";
import { UserStore } from "@/store/constants";

let organizationId;
export const getOrganizationId = (): string | undefined => {
  const store = useStore();
  organizationId = store.getters[UserStore.Getters.ORGANIZATION_ID];
  return organizationId;
}

/**
 * Get all users in the current organization
 */
export const getAllMethods = async (): Promise<IPaymentMethodResult[] | null> => {
  if(!organizationId) getOrganizationId();
  // https://app.chatcaptain.com
  const methods = await fetchCloudRun("https://app.chatcaptain.com/api/billing/organizations/" + organizationId + "/methods", "GET");
  return (methods.methods as IPaymentMethodResult[]);
}

/**
 * Gets an setup intent secret to setup a new payment method with stripe.
 * @returns the setup intent secret as string
 */
export const getSetupIntent = async (): Promise<string> => {
  if(!organizationId) getOrganizationId();
  // https://app.chatcaptain.com
  const response = await fetchCloudRun("https://app.chatcaptain.com/api/billing/organizations/" + organizationId + "/methods/setup", "GET");
  return response.secret as string;
}

/**
 * Creates a new payment method with stripe
 * @param stripeId id of the stripe payment method
 * @param company 
 * @param address 
 * @param holderName 
 * @param vatId 
 */
export const createPaymentMethod = async (stripeId: string, company: string, address: IPaymentMethodResult["address"], holderName: string, vatId?: string) => {
  if(!organizationId) getOrganizationId();
  // https://app.chatcaptain.com
  await fetchCloudRun("https://app.chatcaptain.com/api/billing/organizations/" + organizationId + "/methods", "POST", {}, {
    stripe_id: stripeId,
    company,
    address,
    holder_name: holderName,
    vat_id: vatId
  });
}

/**
 * Updates the specified payment method
 * @param methodId 
 * @param company 
 * @param address 
 * @param holderName 
 * @param vatId 
 */
export const updatePaymentMethod = async (methodId: string, company: string, address: IPaymentMethodResult["address"], holderName: string, vatId?: string) => {
  if(!organizationId) getOrganizationId();
  // https://app.chatcaptain.com
  await fetchCloudRun("https://app.chatcaptain.com/api/billing/organizations/" + organizationId + "/methods/" + methodId, "PUT", {}, {
    company,
    address,
    holder_name: holderName,
    vat_id: vatId
  });
}

/**
 * Deletes the specified payment method
 * @param methodId 
 */
export const deletePaymentMethod = async (methodId: string) => {
  if(!organizationId) getOrganizationId();
  // https://app.chatcaptain.com
  await fetchCloudRun("https://app.chatcaptain.com/api/billing/organizations/" + organizationId + "/methods/" + methodId, "DELETE");
}

/**
 * Gets the default subscription for the organization
 * @returns a ISubscriptionResult object or null if no subscription is present
 */
export const getSubscription = async (): Promise<ISubscriptionResult | null> => {
  if(!organizationId) getOrganizationId();
  // https://app.chatcaptain.com
  try {
    const result = await fetchCloudRun("https://app.chatcaptain.com/api/billing/organizations/" + organizationId + "/subscriptions", "GET");
    return result;
  } catch(err) {
    return null;
  }
}

/**
 * Creates a new subscription
 * @param plan to subscribe
 * @param paymentMethodId id of the payment method
 * @param promoCode (optional) a promo code
 * @returns subscription id as string
 */
export const createSubscription = async (plan: string, paymentMethodId: string, promoCode?: string): Promise<string> => {
  if(!organizationId) getOrganizationId();
  // https://app.chatcaptain.com
  const body = {
    plan,
    payment_method: paymentMethodId
  } as {plan: string; payment_method: string; promo_code?: string};
  if(promoCode) body.promo_code = promoCode;
  const response = await fetchCloudRun("https://app.chatcaptain.com/api/billing/organizations/" + organizationId + "/subscriptions", "POST", {}, body);
  return response.id;
}

/**
 * Cancels the default subscription
 */
export const cancelSubscription = async () => {
  if(!organizationId) getOrganizationId();
  // https://app.chatcaptain.com
  await fetchCloudRun("https://app.chatcaptain.com/api/billing/organizations/" + organizationId + "/subscriptions", "DELETE");
}

/**
 * Reactivates the default subscription (if previously canceled)
 */
export const reactivateSubscription = async () => {
  if(!organizationId) getOrganizationId();
  // https://app.chatcaptain.com
  await fetchCloudRun("https://app.chatcaptain.com/api/billing/organizations/" + organizationId + "/subscriptions/reactivate", "POST");
}

/**
 * Gets all invoices
 * @returns an array of IInvoiceResult objects
 */
export const getAllInvoices = async (): Promise<IInvoiceResult[]> => {
  if(!organizationId) getOrganizationId();
  // https://app.chatcaptain.com
  const response = await fetchCloudRun("https://app.chatcaptain.com/api/billing/organizations/" + organizationId + "/invoices", "GET");
  return response.invoices as IInvoiceResult[];
}

/**
 * Gets a download url 
 * @param id of the invoice
 * @returns signed download url
 */
export const getInvoiceDownloadUrl = async (id: string): Promise<string> => {
  if(!organizationId) getOrganizationId();
  // https://app.chatcaptain.com
  const response = await fetchCloudRun("https://app.chatcaptain.com/api/billing/organizations/" + organizationId + "/invoices/" + id, "GET");
  return response.url as string;
}

/**
 * Gets the estimated invoice
 * @returns IInvoiceResult or null if no subscription is present
 */
export const getEstimatedInvoice = async (): Promise<IEstimatedInvoiceResult | null> => {
  if(!organizationId) getOrganizationId();
  // https://app.chatcaptain.com
  try {
    return await fetchCloudRun("https://app.chatcaptain.com/api/billing/organizations/" + organizationId + "/invoices/estimate", "GET");
  } catch(err) {
    return null;
  }
}

/**
 * Gets a promotion code by it's user friendly name
 * @param promoCode user friendly promo code
 * @returns IPromotionResult object
 */
export const getPromotionCode = async (promoCode: string): Promise<IPromotionResult> => {
  if(!organizationId) getOrganizationId();
  // https://app.chatcaptain.com
  return await fetchCloudRun("https://app.chatcaptain.com/api/billing/organizations/" + organizationId + "/promotions/" + promoCode, "GET");
}

/**
 * Gets an upcoming invoice as an example
 * @param plan the plan to subscribe
 * @param paymentMethodId id of the payment method
 * @param subscription (optional) id of the subscription
 * @param promoCode (optional) user friendly promo code
 * @returns IUpcomingInvoiceResult object
 */
export const getUpcomingInvoice = async (plan: string, paymentMethodId: string, subscription?: string, promoCode?: string): Promise<IUpcomingInvoiceResult> => {
  if(!organizationId) getOrganizationId();
  // https://app.chatcaptain.com
  const params = {
    plan,
    payment_method: paymentMethodId
  } as {plan: string, payment_method: string; subscription?: string; promo_code?: string};
  if(subscription) params.subscription = subscription;
  if(promoCode) params.promo_code = promoCode;
  return await fetchCloudRun("https://app.chatcaptain.com/api/billing/organizations/" + organizationId + "/invoices/upcoming", "GET", params) as IUpcomingInvoiceResult;
}